import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAuth } from 'firebase/auth';
import {
    createEventTickets,
    deleteEventTickets,
    editEventTickets,
    EventTicket,
    getEventTickets,
} from '../../services/eventTickets';
import { RootState } from '../store/store';

interface EventTicketsState {
    tickets: EventTicket[] | null;
    fetchStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
    mutateStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null | undefined;
}

const initialState: EventTicketsState = {
    tickets: null,
    fetchStatus: 'idle',
    mutateStatus: 'idle',
    error: null,
};

export const fetchEventTickets = createAsyncThunk(
    'eventTickets/fetchEventTickets',
    async ({ eventId, includeFees }: { eventId: string; includeFees?: boolean }) => {
        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();
            if (!token) {
                throw new Error('Failed to retrieve Token');
            }
            const res = await getEventTickets(eventId, Boolean(includeFees), token);
            return res;
        } catch (error) {
            throw new Error('Failed to fetch Event Tickets');
        }
    }
);

export const addEventTickets = createAsyncThunk(
    'eventTickets/addEventTickets',
    async ({ eventId, tickets, includeFees = false }: { eventId: string; tickets: EventTicket[]; includeFees?: boolean }) => {
        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();
            if (!token) {
                throw new Error('Failed to retrieve Token');
            }
            await createEventTickets(eventId, tickets, token);
            const res = await getEventTickets(eventId, Boolean(includeFees), token);
            return res;
        } catch (error) {
            throw new Error('Failed to create Event Tickets');
        }
    }
);

export const updateEventTicketsThunk = createAsyncThunk(
    'eventTickets/updateEventTickets',
    async ({ eventId, tickets, includeFees }: { eventId: string; tickets: EventTicket[]; includeFees?: boolean }) => {
        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();
            if (!token) {
                throw new Error('Failed to retrieve Token');
            }
            await editEventTickets(eventId, tickets, token);
            const res = await getEventTickets(eventId, Boolean(includeFees), token);
            return res;
        } catch (error) {
            throw new Error('Failed to edit Event Tickets');
        }
    }
);

export const deleteEventTicketsThunk = createAsyncThunk(
    'eventTickets/deleteEventTickets',
    async ({ eventId, tickets, includeFees }: { eventId: string; tickets: EventTicket[]; includeFees?: boolean }) => {
        try {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();
            if (!token) {
                throw new Error('Failed to retrieve Token');
            }
            await deleteEventTickets(eventId, tickets, token);
            const res = await getEventTickets(eventId, Boolean(includeFees), token);
            return res;
        } catch (error) {
            throw new Error('Failed to delete Event Tickets');
        }
    }
);

const eventTicketsSlice = createSlice({
    name: 'eventTickets',
    initialState,
    reducers: {
        resetEventTicketsMutateStatus(state) {
            state.mutateStatus = 'idle';
        },
        clearEventTickets(state) {
            state.tickets = null;
            state.fetchStatus = 'idle';
            state.mutateStatus = 'idle';
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEventTickets.pending, (state) => {
                state.fetchStatus = 'pending';
                state.error = null;
            })
            .addCase(fetchEventTickets.fulfilled, (state, action) => {
                state.fetchStatus = 'succeeded';
                state.tickets = action.payload?.tickets ?? null;
            })
            .addCase(fetchEventTickets.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(addEventTickets.pending, (state) => {
                state.mutateStatus = 'pending';
                state.error = null;
            })
            .addCase(addEventTickets.fulfilled, (state, action) => {
                state.mutateStatus = 'succeeded';
                state.tickets = action.payload?.tickets ?? state.tickets;
            })
            .addCase(addEventTickets.rejected, (state, action) => {
                state.mutateStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(updateEventTicketsThunk.pending, (state) => {
                state.mutateStatus = 'pending';
                state.error = null;
            })
            .addCase(updateEventTicketsThunk.fulfilled, (state, action) => {
                state.mutateStatus = 'succeeded';
                state.tickets = action.payload?.tickets ?? state.tickets;
            })
            .addCase(updateEventTicketsThunk.rejected, (state, action) => {
                state.mutateStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(deleteEventTicketsThunk.pending, (state) => {
                state.mutateStatus = 'pending';
                state.error = null;
            })
            .addCase(deleteEventTicketsThunk.fulfilled, (state, action) => {
                state.mutateStatus = 'succeeded';
                state.tickets = action.payload?.tickets ?? state.tickets;
            })
            .addCase(deleteEventTicketsThunk.rejected, (state, action) => {
                state.mutateStatus = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { resetEventTicketsMutateStatus, clearEventTickets } = eventTicketsSlice.actions;
export default eventTicketsSlice.reducer;

export const selectEventTickets = (state: RootState) => state.eventTickets.tickets;
export const selectEventTicketsFetchStatus = (state: RootState) => state.eventTickets.fetchStatus;
export const selectEventTicketsMutateStatus = (state: RootState) => state.eventTickets.mutateStatus;
export const selectEventTicketsError = (state: RootState) => state.eventTickets.error;
